import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { BulkIntakeService } from "@/services/bulkIntakeService";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { CheckCircle, XCircle } from "lucide-react";
import { droppedFileReason, droppedSummary } from "./droppedFiles";
import { classifyConfirmationOutcome } from "@/utils/confirmationOutcome";
import { useCountdown } from "@/hooks/useCountdown";

const BulkIntakeConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null); // { confirmed_count, dropped_count, dropped_files }
  const [errorCode, setErrorCode] = useState(null);
  const [resend, setResend] = useState({ status: "idle" });
  const { secondsLeft, start: startCountdown } = useCountdown();

  useEffect(() => {
    const confirmBatch = async () => {
      if (!token) {
        setSuccess(false);
        setMessage("Invalid confirmation link.");
        setLoading(false);
        return;
      }

      try {
        const response = await BulkIntakeService.finalizeBatchConfirmation(token);

        setSuccess(true);
        setResult({
          confirmed_count: response?.confirmed_count ?? 0,
          dropped_count: response?.dropped_count ?? 0,
          dropped_files: response?.dropped_files ?? [],
        });
        setMessage(response?.message || "The batch has been confirmed successfully.");
      } catch (error) {
        const code = error?.response?.data?.error_code;
        const outcome = classifyConfirmationOutcome(code);

        setErrorCode(code);
        setSuccess(outcome.status === "success");
        setMessage(error?.response?.data?.message || (outcome.status === "success" ? "This batch has already been confirmed." : "Confirmation failed."));
      } finally {
        setLoading(false);
      }
    };

    confirmBatch();
  }, [token]);

  const outcome = classifyConfirmationOutcome(errorCode);
  // Once a resend has actually gone out, there is nothing left to offer —
  // no button, just the confirmation that a new link is on its way.
  const showResendButton = !success && outcome.canResend && resend.status !== "sent" && resend.status !== "limit-reached";
  const resendDisabled = resend.status === "sending" || secondsLeft > 0;

  const handleResend = async () => {
    if (!token) return;
    setResend({ status: "sending" });
    try {
      const res = await BulkIntakeService.resendBatchConfirmation(token);
      setResend({
        status: "sent",
        message: res?.message,
        emailMasked: res?.email_masked,
        sendsRemaining: res?.sends_remaining,
      });
    } catch (error) {
      const code = error?.response?.data?.error_code;
      if (code === "CONFIRMATION_RESEND_TOO_SOON") {
        startCountdown(error?.response?.data?.data?.retry_after_seconds || 60);
        setResend({ status: "too-soon", message: error?.response?.data?.message || "Please wait before requesting another link." });
      } else if (code === "CONFIRMATION_RESEND_LIMIT_REACHED") {
        setResend({ status: "limit-reached", message: error?.response?.data?.message || "Please start again." });
      } else {
        setResend({ status: "error", message: error?.response?.data?.message || "Couldn't send a new link. Please try again." });
      }
    }
  };

  return (
    <div className="min-h-[75vh] flex items-center justify-center px-4 sm:px-6 bg-background-primary">
      <Card className="max-w-md w-full p-8 text-center">
        {loading ? (
          <>
            <div className="loader text-text-primary mx-auto mb-4" />
            <h2 className="text-lg text-text-primary font-semibold">Confirming batch…</h2>
          </>
        ) : success ? (
          <>
            <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
            <h2 className="text-xl text-text-primary font-semibold mb-2">Batch Confirmed</h2>
            <p className="text-text-secondary">{message}</p>
            {result && (
              <>
                <p className="text-text-secondary text-sm mt-2">
                  {result.confirmed_count} candidate{result.confirmed_count !== 1 ? "s" : ""} added.
                  {result.dropped_count > 0 && ` ${droppedSummary(result.dropped_count)}`}
                </p>
                {result.dropped_files?.length > 0 && (
                  <ul className="text-xs text-text-secondary text-left mt-2 list-disc pl-5 space-y-0.5">
                    {result.dropped_files.map((f, i) => (
                      <li key={i}>
                        {f.file_name} — {droppedFileReason(f)}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
            <Button onClick={() => navigate("/admin/jobs")} className="mt-5 text-xs bg-text-blue text-text-base rounded-lg px-4 py-2">
              Go to jobs
            </Button>
          </>
        ) : (
          <>
            <XCircle size={64} className="mx-auto text-status-error mb-4" />
            <h2 className="text-xl text-text-primary font-semibold mb-2">Confirmation Failed</h2>
            <p className="text-text-secondary">{message}</p>

            {showResendButton && (
              <div className="mt-4 space-y-2">
                <Button onClick={handleResend} disabled={resendDisabled} className="bg-button-secondary text-text-base rounded-lg px-4 py-2 text-xs">
                  {resend.status === "sending" ? "Sending..." : secondsLeft > 0 ? `Try again in ${secondsLeft}s` : "Resend confirmation link"}
                </Button>

                {(resend.status === "too-soon" || resend.status === "error") && <p className="text-xs text-status-error">{resend.message}</p>}
              </div>
            )}

            {resend.status === "sent" && (
              <p className="mt-4 text-xs text-text-secondary">
                {resend.message}
                {resend.emailMasked ? ` (${resend.emailMasked})` : ""}
              </p>
            )}

            {resend.status === "limit-reached" && <p className="mt-3 text-xs text-status-error">{resend.message}</p>}

            <Button
              onClick={() => navigate("/admin/jobs")}
              className="mt-5 text-xs bg-background-tertiary text-text-primary rounded-lg px-4 py-2 border border-border-primary"
            >
              Back to jobs
            </Button>
          </>
        )}
      </Card>
    </div>
  );
};

export default BulkIntakeConfirmation;
